export type EnvironmentKey =
  | 'production'
  | 'staging'
  | 'integration'
  | 'development'
  | 'preview'
  | 'local'

export type EnvironmentColor =
  | 'error'
  | 'info'
  | 'warning'
  | 'success'
  | 'neutral'

export interface EnvironmentDescriptor {
  key: EnvironmentKey
  label: string
  shortLabel: string
  color: EnvironmentColor
  description: string
  url: string
  isProd: boolean
  /** Whether the environment appears as a target in the switcher dropdown. */
  selectable: boolean
}

const ENVIRONMENTS: Record<EnvironmentKey, EnvironmentDescriptor> = {
  production: {
    key: 'production',
    label: 'Production',
    shortLabel: 'PROD',
    color: 'error',
    description: 'Live customer environment',
    url: 'https://admin.rialtic.app',
    isProd: true,
    selectable: true,
  },
  staging: {
    key: 'staging',
    label: 'Staging',
    shortLabel: 'STAGING',
    color: 'info',
    description: 'Pre-release testing',
    url: 'https://admin.staging.rialtic.app',
    isProd: false,
    selectable: true,
  },
  integration: {
    key: 'integration',
    label: 'Integration',
    shortLabel: 'INT',
    color: 'warning',
    description: 'Content validation & QA',
    url: 'https://admin.integration.rialtic.app',
    isProd: false,
    selectable: true,
  },
  development: {
    key: 'development',
    label: 'Development',
    shortLabel: 'DEV',
    color: 'success',
    description: 'Team dev environment',
    url: 'https://admin.development.rialtic.app',
    isProd: false,
    selectable: true,
  },
  preview: {
    key: 'preview',
    label: 'Preview',
    shortLabel: 'PREVIEW',
    color: 'neutral',
    description: 'Ephemeral preview deployment',
    url: '',
    isProd: false,
    selectable: false,
  },
  local: {
    key: 'local',
    label: 'Local',
    shortLabel: 'LOCAL',
    color: 'success',
    description: 'Local development',
    url: '',
    isProd: false,
    selectable: false,
  },
}

/** Deployed environments shown in the switcher dropdown, in PROD→DEV order. */
export const selectableEnvironments: EnvironmentDescriptor[] = Object.values(
  ENVIRONMENTS,
).filter((env) => env.selectable)

function hostFromUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/.*$/, '')
}

/**
 * Resolve the active environment from the hostname alone (the build-stamped
 * ENVIRONMENT_NAME is unreliable — e.g. the integration deploy stamps
 * "development"). A "preview" host wins, then an exact match against a
 * deployed env host; anything else (localhost, unknown hosts) is `local`.
 */
export function resolveEnvironment(
  hostname: string | undefined,
): EnvironmentDescriptor {
  if (!hostname) return ENVIRONMENTS.local
  if (hostname.includes('preview')) return ENVIRONMENTS.preview
  const match = selectableEnvironments.find(
    (env) => hostFromUrl(env.url) === hostname,
  )
  return match ?? ENVIRONMENTS.local
}

/** Build the target URL for a selectable environment, preserving the given path. */
export function buildEnvUrl(env: EnvironmentDescriptor, path: string): string {
  const base = env.url.replace(/\/$/, '')
  if (!base) return path || '/'
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${base}${suffix}`
}
